import { useState, useRef, useEffect, useCallback } from "react";
import "@/App.css";

// ACCURATE stain colors from the Goof Proof wheel
const STAIN_COLORS = [
  { name: "Natural / Light Oak", hex: "#C7A47E" },
  { name: "Pecan", hex: "#7F543B" },
  { name: "Dark Walnut", hex: "#3E281C" },
  { name: "Mission Brown", hex: "#342E2A" },
  { name: "Burnt Hickory", hex: "#221B12" },
  { name: "Mahogany", hex: "#7A1A1C" },
  { name: "Redwood", hex: "#A3251C" },
  { name: "Natural Cedar", hex: "#BD633F" },
];

// Assets
const LOGO_URL = "/logo.png";
const FENCE_ORIGINAL_URL = "/fence-original.jpg";
const FENCE_MASK_URL = "/fence-mask.png";

// Convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const FenceVisualizer = () => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const canvasRef = useRef(null);
  const originalImageRef = useRef(null);
  const maskImageRef = useRef(null);
  const maskDataRef = useRef(null);

  // Load both images
  useEffect(() => {
    let loadedCount = 0;
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount === 2) {
        initializeCanvas();
      }
    };

    // Load original fence photo
    const originalImg = new Image();
    originalImg.crossOrigin = "anonymous";
    originalImg.onload = () => {
      originalImageRef.current = originalImg;
      checkLoaded();
    };
    originalImg.src = FENCE_ORIGINAL_URL;

    // Load fence mask
    const maskImg = new Image();
    maskImg.crossOrigin = "anonymous";
    maskImg.onload = () => {
      maskImageRef.current = maskImg;
      checkLoaded();
    };
    maskImg.src = FENCE_MASK_URL;
  }, []);

  // Initialize canvas with both images loaded
  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    const original = originalImageRef.current;
    const mask = maskImageRef.current;
    
    if (!canvas || !original || !mask) return;

    // Set canvas to original image size
    canvas.width = original.width;
    canvas.height = original.height;

    const ctx = canvas.getContext('2d');
    
    // Draw original background first
    ctx.drawImage(original, 0, 0);
    
    // Draw mask on top as the default view (unstained fence)
    ctx.drawImage(mask, 0, 0, original.width, original.height);

    // Extract mask data for staining
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = original.width;
    tempCanvas.height = original.height;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.drawImage(mask, 0, 0, original.width, original.height);
    maskDataRef.current = tempCtx.getImageData(0, 0, original.width, original.height);

    setImagesLoaded(true);
  };

  // Apply stain using the mask
  const applyStain = useCallback((color) => {
    const canvas = canvasRef.current;
    const original = originalImageRef.current;
    const maskData = maskDataRef.current;
    
    if (!canvas || !original || !maskData) return;

    setIsProcessing(true);

    requestAnimationFrame(() => {
      const ctx = canvas.getContext('2d');
      
      // Draw original background first
      ctx.drawImage(original, 0, 0);

      if (color) {
        const stain = hexToRgb(color.hex);
        
        // Get current image data (original)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        const maskPixels = maskData.data;
        
        // Natural wood highlight color (warm cedar/pine tone that shows through stain)
        const woodHighlight = { r: 180, g: 140, b: 95 };

        for (let i = 0; i < pixels.length; i += 4) {
          const maskAlpha = maskPixels[i + 3];
          
          if (maskAlpha > 20) {
            // Get mask pixel color (the fence wood grain)
            const maskR = maskPixels[i];
            const maskG = maskPixels[i + 1];
            const maskB = maskPixels[i + 2];
            
            // Calculate wood luminance - identifies grain highlights
            const woodLum = (0.299 * maskR + 0.587 * maskG + 0.114 * maskB) / 255;
            
            // Multiply blend for base stain effect
            const multiplyR = (maskR * stain.r) / 255;
            const multiplyG = (maskG * stain.g) / 255;
            const multiplyB = (maskB * stain.b) / 255;
            
            // Strong stain coverage (80% stain, 20% wood)
            const stainOpacity = 0.8;
            let finalR = multiplyR * stainOpacity + maskR * (1 - stainOpacity) * 0.5;
            let finalG = multiplyG * stainOpacity + maskG * (1 - stainOpacity) * 0.4;
            let finalB = multiplyB * stainOpacity + maskB * (1 - stainOpacity) * 0.3;
            
            // In highlight areas (raised grain), paint in wood tone showing through
            if (woodLum > 0.5) {
              const woodShowThrough = Math.pow((woodLum - 0.5) / 0.5, 0.8) * 0.35;
              finalR = finalR * (1 - woodShowThrough) + woodHighlight.r * woodShowThrough * woodLum;
              finalG = finalG * (1 - woodShowThrough) + woodHighlight.g * woodShowThrough * woodLum;
              finalB = finalB * (1 - woodShowThrough) + woodHighlight.b * woodShowThrough * woodLum;
            }
            
            // Grain texture depth
            const grainTexture = (woodLum - 0.5) * 30;
            finalR = Math.min(255, Math.max(0, finalR + grainTexture));
            finalG = Math.min(255, Math.max(0, finalG + grainTexture * 0.7));
            finalB = Math.min(255, Math.max(0, finalB + grainTexture * 0.5));

            // Apply with mask alpha for soft edges
            const alphaFactor = maskAlpha / 255;
            pixels[i] = Math.round(pixels[i] * (1 - alphaFactor) + finalR * alphaFactor);
            pixels[i + 1] = Math.round(pixels[i + 1] * (1 - alphaFactor) + finalG * alphaFactor);
            pixels[i + 2] = Math.round(pixels[i + 2] * (1 - alphaFactor) + finalB * alphaFactor);
          }
        }

        ctx.putImageData(imageData, 0, 0);
      }

      setIsProcessing(false);
    });
  }, []);

  // Handle color selection
  const handleColorSelect = (color) => {
    setSelectedColor(color);
    applyStain(color);
  };

  // Reset to original (unstained mask view)
  const handleReset = () => {
    setSelectedColor(null);
    const canvas = canvasRef.current;
    const original = originalImageRef.current;
    const mask = maskImageRef.current;
    if (canvas && original && mask) {
      const ctx = canvas.getContext('2d');
      ctx.drawImage(original, 0, 0);
      ctx.drawImage(mask, 0, 0, original.width, original.height);
    }
  };

  return (
    <div className="visualizer-app">
      {/* Header */}
      <header className="header" data-testid="header">
        <div className="header-content">
          <a href="https://mendelfamilyfencing.com" className="logo-link" data-testid="logo-link">
            <img src={LOGO_URL} alt="Mendel Family Fencing" className="logo" />
          </a>
          <a 
            href="https://mendelfamilyfencing.com/contact" 
            className="cta-button"
            data-testid="cta-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get a Free Quote
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="visualizer-container" data-testid="visualizer-container">
          {/* Visualizer Section */}
          <div className="visualizer-section">
            <div className="canvas-wrapper" data-testid="canvas-wrapper">
              <canvas 
                ref={canvasRef} 
                className="fence-canvas"
                data-testid="fence-canvas"
              />
              {!imagesLoaded && (
                <div className="loading-overlay" data-testid="loading-overlay">
                  <div className="loading-spinner"></div>
                  <span>Loading fence image...</span>
                </div>
              )}
              {isProcessing && (
                <div className="processing-indicator" data-testid="processing-indicator">
                  <div className="loading-spinner small"></div>
                </div>
              )}
            </div>
            
            {selectedColor && (
              <div className="selected-info" data-testid="selected-info">
                <span className="selected-label">Selected:</span>
                <span 
                  className="selected-color-chip"
                  style={{ backgroundColor: selectedColor.hex }}
                ></span>
                <span className="selected-name">{selectedColor.name}</span>
              </div>
            )}
          </div>

          {/* Controls Section */}
          <div className="controls-section" data-testid="controls-section">
            <div className="controls-header">
              <h2 className="controls-title">Choose Your Stain</h2>
              <p className="controls-subtitle">Select a color to preview on your fence</p>
            </div>
            
            <div className="color-grid" data-testid="color-grid">
              {STAIN_COLORS.map((color) => (
                <div key={color.name} className="swatch-container">
                  <button
                    className={`color-swatch ${selectedColor?.name === color.name ? 'active' : ''}`}
                    style={{ backgroundColor: color.hex }}
                    onClick={() => handleColorSelect(color)}
                    data-testid={`swatch-${color.name.toLowerCase().replace(/[\s\/]+/g, '-').replace(/-+/g, '-')}`}
                    aria-label={`Select ${color.name} stain`}
                    disabled={!imagesLoaded}
                  >
                    <span className="swatch-inner"></span>
                  </button>
                  <span className={`color-label ${selectedColor?.name === color.name ? 'active' : ''}`}>
                    {color.name}
                  </span>
                </div>
              ))}
            </div>
            
            <p className="stain-disclaimer">Real stain results can vary based on wood species and board age</p>

            {selectedColor && (
              <button 
                className="reset-button"
                onClick={handleReset}
                data-testid="reset-button"
              >
                Reset to Original
              </button>
            )}

            <div className="mobile-cta">
              <a 
                href="https://mendelfamilyfencing.com/contact" 
                className="cta-button full-width"
                data-testid="mobile-cta-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get a Free Quote
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <p>© {new Date().getFullYear()} Mendel Family Fencing. All rights reserved.</p>
      </footer>

      {/* Powered by SignalPoint Systems Badge */}
      <a
        href="https://signalpointsystems.com"
        target="_blank"
        rel="noopener noreferrer"
        className="signalpoint-badge"
        data-testid="signalpoint-badge"
      >
        <img src="/signalpoint-logo.png" alt="SignalPoint Systems" className="signalpoint-logo" />
        <span>Powered by SignalPoint Systems</span>
      </a>
    </div>
  );
};

function App() {
  return <FenceVisualizer />;
}

export default App;
